import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

interface QuizQuestion {
  id: string
  question_text: string
  options: string[]
  rationale?: string | null
  section?: {
    id: string
    name: string
  } | null
}

interface QuizData {
  id: string
  name: string
  questions: QuizQuestion[]
}

type QuizSectionSummary = {
  id: string
  name: string
  total: number
  answered: number
  questionIndexes: number[]
}

type QuestionStatus = "answered" | "unanswered"
interface QuizResults {
  score: number
  total_questions: number
  percentile?: number | null
  can_review?: boolean
  show_result_mode?: string
  result_date?: string | null
  show_rationale_mode?: string
  attempt_id?: string
}

interface QuizState {
  currentQuestionIndex: number
  answers: Record<string, number>
  startTime: number
  timeRemaining: number | null
}

interface QuizInterfaceContextValue {
  // State
  quizState: QuizState
  selectedAnswerIndex: number | null
  showConfirmSubmit: boolean
  currentQuestion: QuizQuestion | undefined

  // Computed values
  totalQuestions: number
  answeredCount: number
  unansweredCount: number
  sections: QuizSectionSummary[]
  activeSectionId: string | null
  questionStatuses: QuestionStatus[]
  canNavigateQuestions: boolean

  // Actions
  handleAnswerSelect: (answerIndex: number) => void
  handleNext: () => void
  handlePrevious: () => void
  handleSubmit: () => void
  isSavingAnswer: boolean
  answerSaveError: string | null
  setShowConfirmSubmit: (show: boolean) => void
  formatTime: (seconds: number) => string
  navigateToSection: (sectionId: string) => void
  navigateToQuestion: (index: number) => void
}

const QuizInterfaceContext = createContext<
  QuizInterfaceContextValue | undefined
>(undefined)

interface QuizInterfaceProviderProps {
  quiz: QuizData
  attemptId: string
  timeLimit?: number | null
  initialAnswers?: Record<string, number>
  initialTimeRemaining?: number | null
  initialQuestionIndex?: number
  onAnswerSelect: (
    questionId: string,
    answerIndex: number,
    timeSpent: number,
  ) => void | Promise<void>
  onSubmit: (params?: {
    finalQuestionId: string
    finalAnswerIndex: number
    timeSpent: number
  }) => void
  isSubmitted?: boolean
  isSubmitting?: boolean
  onQuestionChange?: (index: number) => void
  showRationaleMode?: string
  onNavigateToReview?: (params: {
    questionIndex: number
    questionId: string
  }) => void
  children: ReactNode
}

export function QuizInterfaceProvider({
  quiz,
  attemptId,
  timeLimit,
  initialAnswers,
  initialTimeRemaining,
  initialQuestionIndex,
  onAnswerSelect,
  onSubmit,
  isSubmitted = false,
  isSubmitting = false,
  onQuestionChange,
  showRationaleMode,
  onNavigateToReview,
  children,
}: QuizInterfaceProviderProps) {
  const answersFromProps = useMemo(() => initialAnswers ?? {}, [initialAnswers])
  const derivedTimeRemaining = useMemo(() => {
    if (typeof initialTimeRemaining === "number") {
      return initialTimeRemaining
    }
    if (timeLimit) {
      return timeLimit * 60
    }
    return null
  }, [initialTimeRemaining, timeLimit])

  const totalQuestions = quiz.questions.length
  const clampQuestionIndex = (index?: number | null) => {
    if (totalQuestions === 0) return 0
    if (typeof index !== "number" || Number.isNaN(index)) {
      return 0
    }
    return Math.min(Math.max(index, 0), totalQuestions - 1)
  }

  const normalizedInitialQuestionIndex =
    clampQuestionIndex(initialQuestionIndex)

  const [quizState, setQuizState] = useState<QuizState>(() => ({
    currentQuestionIndex: normalizedInitialQuestionIndex,
    answers: answersFromProps,
    startTime: Date.now(),
    timeRemaining: derivedTimeRemaining,
  }))

  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<number | null>(
    () => {
      const firstQuestion = quiz.questions[0]
      if (!firstQuestion) {
        return null
      }
      const existing = answersFromProps[firstQuestion.id]
      return typeof existing === "number" ? existing : null
    },
  )
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false)
  const [isSavingAnswer, setIsSavingAnswer] = useState(false)
  const [answerSaveError, setAnswerSaveError] = useState<string | null>(null)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(() => {
    const firstWithSection = quiz.questions.find(
      (question) => question.section?.id,
    )
    return firstWithSection?.section?.id ?? null
  })
  const lastQuestionSelectionRef = useRef<{
    questionId: string
    answerIndex: number
  } | null>(null)
  const lastAttemptIdRef = useRef<string | null>(null)
  const lastQuizIdRef = useRef<string | null>(null)
  // Guards against duplicate submits within a single attempt: a held/stuck
  // Enter key (keydown auto-repeat) or a render loop can otherwise call
  // onSubmit hundreds of times in a burst, freezing the tab and prematurely
  // finalizing the attempt. Set synchronously so it stops same-tick bursts;
  // re-armed when the confirm dialog reopens (legitimate retry) or the
  // attempt changes.
  const submitInFlightRef = useRef(false)
  const answerSaveInFlightRef = useRef(false)

  // Reset state when attempt or quiz changes
  useEffect(() => {
    if (!attemptId) {
      return
    }

    const attemptChanged = lastAttemptIdRef.current !== attemptId
    const quizChanged = lastQuizIdRef.current !== quiz.id

    if (!attemptChanged && !quizChanged) {
      return
    }

    lastAttemptIdRef.current = attemptId
    lastQuizIdRef.current = quiz.id
    submitInFlightRef.current = false

    setQuizState({
      currentQuestionIndex: normalizedInitialQuestionIndex,
      answers: answersFromProps,
      startTime: Date.now(),
      timeRemaining: derivedTimeRemaining,
    })

    const initialQuestion = quiz.questions[0]
    if (initialQuestion) {
      const existing = answersFromProps[initialQuestion.id]
      setSelectedAnswerIndex(typeof existing === "number" ? existing : null)
    } else {
      setSelectedAnswerIndex(null)
    }

    const firstWithSection = quiz.questions.find(
      (question) => question.section?.id,
    )
    setActiveSectionId(firstWithSection?.section?.id ?? null)
  }, [
    attemptId,
    quiz.id,
    quiz.questions,
    answersFromProps,
    derivedTimeRemaining,
    normalizedInitialQuestionIndex,
  ])

  // Sync answers from props
  useEffect(() => {
    setQuizState((prev) => {
      const prevKeys = Object.keys(prev.answers)
      const nextKeys = Object.keys(answersFromProps)

      if (
        prevKeys.length === nextKeys.length &&
        prevKeys.every((key) => prev.answers[key] === answersFromProps[key]) &&
        nextKeys.every((key) => answersFromProps[key] === prev.answers[key])
      ) {
        return prev
      }

      return {
        ...prev,
        answers: answersFromProps,
      }
    })
  }, [answersFromProps])

  // Sync question index from props
  useEffect(() => {
    if (initialQuestionIndex === undefined) {
      return
    }
    const normalizedIndex = normalizedInitialQuestionIndex
    setQuizState((prev) => {
      if (prev.currentQuestionIndex === normalizedIndex) {
        return prev
      }
      return {
        ...prev,
        currentQuestionIndex: normalizedIndex,
        startTime: Date.now(),
      }
    })
  }, [initialQuestionIndex, normalizedInitialQuestionIndex])

  // Sync time remaining from props
  useEffect(() => {
    if (derivedTimeRemaining === null) {
      return
    }
    setQuizState((prev) => ({
      ...prev,
      timeRemaining: derivedTimeRemaining,
    }))
  }, [derivedTimeRemaining])

  // Update selected answer when question changes
  useEffect(() => {
    const question = quiz.questions[quizState.currentQuestionIndex]
    if (!question) return
    const existing = quizState.answers[question.id]
    setSelectedAnswerIndex(typeof existing === "number" ? existing : null)
    lastQuestionSelectionRef.current = null
  }, [quizState.currentQuestionIndex, quizState.answers, quiz.questions])

  // Re-arm the submit guard when a prior submit settles without success, so a
  // failed network call can be retried. A successful submit leaves the guard
  // latched (the interface is leaving the quiz), and the guard is also reset
  // when the attempt changes.
  useEffect(() => {
    if (!isSubmitting && !isSubmitted) {
      submitInFlightRef.current = false
    }
  }, [isSubmitting, isSubmitted])

  // Timer countdown
  useEffect(() => {
    if (
      quizState.timeRemaining === null ||
      quizState.timeRemaining <= 0 ||
      isSubmitted
    ) {
      return
    }

    const timer = setInterval(() => {
      setQuizState((prev) => {
        const newTimeRemaining = prev.timeRemaining! - 1

        // Auto-submit when time runs out
        if (newTimeRemaining <= 0) {
          handleSubmitQuiz()
          return { ...prev, timeRemaining: 0 }
        }

        return { ...prev, timeRemaining: newTimeRemaining }
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [quizState.timeRemaining, isSubmitted])

  const currentQuestion = quiz.questions[quizState.currentQuestionIndex]
  const recordedAnsweredCount = Object.keys(quizState.answers).length
  const isCurrentRecorded =
    currentQuestion && quizState.answers[currentQuestion.id] !== undefined
  const pendingCurrent =
    selectedAnswerIndex !== null && !isCurrentRecorded ? 1 : 0
  const answeredCount = recordedAnsweredCount + pendingCurrent
  const unansweredCount = Math.max(totalQuestions - answeredCount, 0)

  const questionStatuses = useMemo<QuestionStatus[]>(() => {
    return quiz.questions.map((question) =>
      quizState.answers[question.id] !== undefined ? "answered" : "unanswered",
    )
  }, [quiz.questions, quizState.answers])

  const sections = useMemo<QuizSectionSummary[]>(() => {
    const summaryMap = new Map<string, QuizSectionSummary>()
    quiz.questions.forEach((question, index) => {
      const section = question.section
      if (!section) {
        return
      }
      const status = questionStatuses[index]
      const existing = summaryMap.get(section.id)
      if (!existing) {
        summaryMap.set(section.id, {
          id: section.id,
          name: section.name ?? "Section",
          total: 1,
          answered: status === "answered" ? 1 : 0,
          questionIndexes: [index],
        })
        return
      }
      existing.total += 1
      existing.questionIndexes.push(index)
      if (status === "answered") {
        existing.answered += 1
      }
    })
    return Array.from(summaryMap.values())
  }, [quiz.questions, questionStatuses])

  useEffect(() => {
    if (sections.length === 0) {
      if (activeSectionId !== null) {
        setActiveSectionId(null)
      }
      return
    }
    const hasActive = sections.some((section) => section.id === activeSectionId)
    if (!hasActive) {
      setActiveSectionId(sections[0].id)
    }
  }, [sections, activeSectionId])

  useEffect(() => {
    if (sections.length === 0) {
      return
    }
    const currentSectionId =
      quiz.questions[quizState.currentQuestionIndex]?.section?.id
    if (currentSectionId && currentSectionId !== activeSectionId) {
      setActiveSectionId(currentSectionId)
    }
  }, [
    sections.length,
    quiz.questions,
    quizState.currentQuestionIndex,
    activeSectionId,
  ])

  const canNavigateQuestions =
    showRationaleMode !== "after_each" && !isSubmitted

  const navigateToQuestion = useCallback(
    async (targetIndex: number) => {
      if (!canNavigateQuestions) {
        return
      }
      const nextIndex = clampQuestionIndex(targetIndex)
      if (nextIndex === quizState.currentQuestionIndex) {
        return
      }

      const questionId = currentQuestion?.id
      const recordedIndex = selectedAnswerIndex
      const timeSpent = Math.floor((Date.now() - quizState.startTime) / 1000)

      if (answerSaveInFlightRef.current) {
        return
      }

      if (questionId !== undefined && recordedIndex !== null) {
        setQuizState((prev) => ({
          ...prev,
          answers: { ...prev.answers, [questionId]: recordedIndex },
        }))
        setAnswerSaveError(null)
        answerSaveInFlightRef.current = true
        setIsSavingAnswer(true)
        try {
          await onAnswerSelect(questionId, recordedIndex, timeSpent)
        } catch {
          setAnswerSaveError(
            "Your answer could not be saved. Please try again before continuing.",
          )
          return
        } finally {
          answerSaveInFlightRef.current = false
          setIsSavingAnswer(false)
        }
      }

      setQuizState((prev) => ({
        ...prev,
        currentQuestionIndex: nextIndex,
        startTime: Date.now(),
      }))

      onQuestionChange?.(nextIndex)
    },
    [
      canNavigateQuestions,
      clampQuestionIndex,
      currentQuestion?.id,
      selectedAnswerIndex,
      quizState.currentQuestionIndex,
      quizState.startTime,
      onAnswerSelect,
      onQuestionChange,
    ],
  )

  const navigateToSection = useCallback(
    (sectionId: string) => {
      if (!canNavigateQuestions) {
        return
      }

      const targetSection = sections.find((section) => section.id === sectionId)
      if (!targetSection) {
        return
      }

      setActiveSectionId(sectionId)
      const nextQuestionIndex =
        targetSection.questionIndexes.find(
          (index) => questionStatuses[index] === "unanswered",
        ) ?? targetSection.questionIndexes[0]

      if (typeof nextQuestionIndex === "number") {
        navigateToQuestion(nextQuestionIndex)
      }
    },
    [canNavigateQuestions, navigateToQuestion, questionStatuses, sections],
  )
  const handleAnswerSelect = useCallback(
    (answerIndex: number) => {
      if (answerSaveInFlightRef.current) {
        return
      }

      setSelectedAnswerIndex(answerIndex)
      const questionId = currentQuestion?.id

      if (!questionId) {
        return
      }

      setQuizState((prev) => {
        if (prev.answers[questionId] === answerIndex) {
          return prev
        }
        return {
          ...prev,
          answers: { ...prev.answers, [questionId]: answerIndex },
        }
      })
    },
    [currentQuestion?.id],
  )

  const handleNext = useCallback(async () => {
    if (quizState.currentQuestionIndex < quiz.questions.length - 1) {
      const questionId = currentQuestion?.id
      const recordedIndex = selectedAnswerIndex
      const timeSpent = Math.floor((Date.now() - quizState.startTime) / 1000)
      if (answerSaveInFlightRef.current) {
        return
      }

      if (questionId !== undefined && recordedIndex !== null) {
        setQuizState((prev) => ({
          ...prev,
          answers: { ...prev.answers, [questionId]: recordedIndex },
        }))
        setAnswerSaveError(null)
        answerSaveInFlightRef.current = true
        setIsSavingAnswer(true)
        try {
          await onAnswerSelect(questionId, recordedIndex, timeSpent)
        } catch {
          setAnswerSaveError(
            "Your answer could not be saved. Please try again before continuing.",
          )
          return
        } finally {
          answerSaveInFlightRef.current = false
          setIsSavingAnswer(false)
        }
      }

      setQuizState((prev) => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
        startTime: Date.now(),
      }))

      // Navigate to review page if in after_each mode
      if (
        showRationaleMode === "after_each" &&
        onNavigateToReview &&
        questionId
      ) {
        const navigateToReview = () =>
          onNavigateToReview({
            questionIndex: quizState.currentQuestionIndex,
            questionId,
          })
        navigateToReview()
      } else {
        // Normal navigation to next question
        const nextIndex = quizState.currentQuestionIndex + 1
        setSelectedAnswerIndex(null)
        onQuestionChange?.(nextIndex)
      }
    }
  }, [
    quizState.currentQuestionIndex,
    quizState.startTime,
    quiz.questions.length,
    currentQuestion?.id,
    selectedAnswerIndex,
    onAnswerSelect,
    showRationaleMode,
    onNavigateToReview,
    onQuestionChange,
  ])

  const handlePrevious = useCallback(() => {
    if (quizState.currentQuestionIndex > 0) {
      const previousIndex = quizState.currentQuestionIndex - 1
      setQuizState((prev) => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex - 1,
        startTime: Date.now(),
      }))
      onQuestionChange?.(previousIndex)
    }
  }, [quizState.currentQuestionIndex, onQuestionChange])

  const handleSubmitQuiz = useCallback(() => {
    if (!attemptId) return
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true
    const questionId = currentQuestion?.id
    const recordedIndex = selectedAnswerIndex
    const timeSpent = Math.floor((Date.now() - quizState.startTime) / 1000)
    if (
      showRationaleMode === "after_each" &&
      questionId &&
      recordedIndex !== null
    ) {
      onSubmit({
        finalQuestionId: questionId,
        finalAnswerIndex: recordedIndex,
        timeSpent,
      })
      setShowConfirmSubmit(false)
      return
    }
    onSubmit()
    setShowConfirmSubmit(false)
  }, [
    attemptId,
    currentQuestion?.id,
    selectedAnswerIndex,
    quizState.startTime,
    showRationaleMode,
    onSubmit,
  ])

  const handleSubmit = useCallback(async () => {
    if (answerSaveInFlightRef.current) {
      return
    }

    const questionId = currentQuestion?.id
    const recordedIndex = selectedAnswerIndex
    const timeSpent = Math.floor((Date.now() - quizState.startTime) / 1000)

    let answerSubmission: void | PromiseLike<void> | undefined

    if (
      questionId !== undefined &&
      recordedIndex !== null &&
      quizState.answers[questionId] !== recordedIndex
    ) {
      setQuizState((prev) => ({
        ...prev,
        answers: { ...prev.answers, [questionId]: recordedIndex },
      }))
      answerSubmission = onAnswerSelect(questionId, recordedIndex, timeSpent)
    } else if (
      questionId !== undefined &&
      recordedIndex !== null &&
      quizState.answers[questionId] === recordedIndex
    ) {
      const lastSelection = lastQuestionSelectionRef.current
      const alreadyRecordedOnSelection =
        lastSelection &&
        lastSelection.questionId === questionId &&
        lastSelection.answerIndex === recordedIndex

      if (!alreadyRecordedOnSelection) {
        answerSubmission = onAnswerSelect(questionId, recordedIndex, timeSpent)
      }
    }
    lastQuestionSelectionRef.current = null

    if (
      answerSubmission &&
      typeof (answerSubmission as PromiseLike<void>).then === "function"
    ) {
      setAnswerSaveError(null)
      answerSaveInFlightRef.current = true
      setIsSavingAnswer(true)
      try {
        await answerSubmission
      } catch {
        setAnswerSaveError(
          "Your answer could not be saved. Please retry before submitting.",
        )
        return
      } finally {
        answerSaveInFlightRef.current = false
        setIsSavingAnswer(false)
      }
    }

    // Wait for the last-question answer to persist before finalizing the
    // attempt. Without this, the submit-quiz request can commit `completed`
    // before the answer POST arrives and the backend rejects the late answer
    // with 400, silently dropping the student's selection.
    handleSubmitQuiz()
  }, [
    currentQuestion?.id,
    selectedAnswerIndex,
    quizState.startTime,
    quizState.answers,
    onAnswerSelect,
    handleSubmitQuiz,
  ])

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }, [])

  const value: QuizInterfaceContextValue = {
    quizState,
    selectedAnswerIndex,
    showConfirmSubmit,
    currentQuestion,
    totalQuestions,
    answeredCount,
    unansweredCount,
    sections,
    activeSectionId,
    questionStatuses,
    canNavigateQuestions,
    handleAnswerSelect,
    handleNext,
    handlePrevious,
    handleSubmit,
    isSavingAnswer,
    answerSaveError,
    setShowConfirmSubmit,
    formatTime,
    navigateToSection,
    navigateToQuestion,
  }

  return (
    <QuizInterfaceContext.Provider value={value}>
      {children}
    </QuizInterfaceContext.Provider>
  )
}

export function useQuizInterface() {
  const context = useContext(QuizInterfaceContext)
  if (!context) {
    throw new Error(
      "useQuizInterface must be used within QuizInterfaceProvider",
    )
  }
  return context
}

export type {
  QuizQuestion,
  QuizData,
  QuizResults,
  QuizSectionSummary,
  QuestionStatus,
}
